import { COPY } from "../copy.js";
import { COLOR, css } from "../paint.js";
import type { Route } from "../route.js";

/**
 * The frame the public pages sit in: a header with the mark, and a footer.
 *
 * Separate from the app shell because the audiences differ. A landing page is read by an adult
 * deciding whether to trust us; the game surface is touched by a child. They share the palette and
 * the type scale so the product reads as one thing, and share nothing else — a marketing header
 * over a game would be clutter competing with the only thing on screen that matters.
 *
 * Mobile-first: the header is sized for a thumb at 390px and only gains breathing room above that.
 */

/** The K mark, drawn rather than loaded, so the header costs no request. */
export function Mark({ size = 34 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" role="presentation">
      <rect x="16" y="14" width="17" height="72" rx="8.5" fill={css(COLOR.turquoise)} />
      <path d="M40 52 L70 16 L86 30 L56 58 Z" fill={css(COLOR.sunny)} />
      <path d="M40 52 L70 88 L86 74 L56 46 Z" fill={css(COLOR.mint)} />
      <circle cx="78" cy="20" r="10" fill={css(COLOR.sunny)} />
    </svg>
  );
}

export function SiteHeader({
  navigate,
  home,
}: {
  navigate: (route: Route) => void;
  /**
   * Where a signed-in visitor's own surface is, or null when nobody is signed in.
   *
   * A route rather than a boolean, because the two roles have different homes: sending a teacher to
   * the game shelf was the bug a boolean could not express.
   */
  home: Route | null;
}) {
  // Registering is offered only while nobody is signed in. It is a link that says "you have no
  // account", which is a strange thing to say to somebody whose class is one tap to the right.
  const showApply = home === null;

  return (
    <header className="mx-auto max-w-[1040px] px-lg py-md md:px-xl md:py-lg">
      <div className="flex items-center justify-between gap-md">
        <button
          onClick={() => navigate({ kind: "landing" })}
          className="flex items-center gap-sm"
          aria-label={COPY.site.brand}
        >
          <Mark />
          <span className="text-heading font-extrabold">{COPY.site.brand}</span>
        </button>

        <nav className="flex items-center gap-xs">
          {/*
            * On a phone this row is the mark, the wordmark and the enter button only — a text link
            * beside them either wraps (the one thing the button must never do) or shrinks past
            * legibility. `NavRow` below repeats both links life-size on a row of their own instead of
            * hiding them; above `sm` there is room in this row and `NavRow` disappears.
            */}
          <button
            onClick={() => navigate({ kind: "about" })}
            className="hidden min-h-11 rounded-pill px-md py-sm text-small text-ink sm:block"
          >
            {COPY.site.nav.about}
          </button>
          {showApply && (
            <button
              onClick={() => navigate({ kind: "apply" })}
              className="hidden min-h-11 rounded-pill px-md py-sm text-small text-ink sm:block"
            >
              {COPY.site.nav.apply}
            </button>
          )}
          <button
            onClick={() => navigate(home ?? { kind: "enter" })}
            className="min-h-11 rounded-pill bg-grape px-lg py-sm text-small font-bold text-card"
          >
            {home === null
              ? COPY.site.nav.enter
              : home.kind === "class"
                ? COPY.site.nav.classroom
                : COPY.site.nav.play}
          </button>
        </nav>
      </div>

      {/* The phone row: full-size, tappable pills rather than the cramped text `nav` above hides. */}
      <nav className="mt-sm flex items-center gap-sm sm:hidden">
        <button
          onClick={() => navigate({ kind: "about" })}
          className="min-h-11 flex-1 rounded-pill bg-ink/6 py-sm text-small font-bold text-ink"
        >
          {COPY.site.nav.about}
        </button>
        {showApply && (
          <button
            onClick={() => navigate({ kind: "apply" })}
            className="min-h-11 flex-1 rounded-pill bg-grape/12 py-sm text-small font-bold text-grape"
          >
            {COPY.site.nav.apply}
          </button>
        )}
      </nav>
    </header>
  );
}

/**
 * The Enamad trust seal.
 *
 * The link, the image and the `id`/`Code` query params are issued by enamad.ir for this domain and
 * kept verbatim rather than assembled — enamad.ir is the one checking this badge, so a value it does
 * not recognise fails their check silently rather than loudly. `code` on the `img` is their attribute,
 * not ours; React passes an unrecognised lowercase attribute straight through to the DOM.
 */
function EnamadBadge() {
  return (
    <a
      href="https://trustseal.enamad.ir/?id=7272343&Code=cOtSN7wMbB6CVNFOfGbN4ZQHPTjttU0K"
      target="_blank"
      rel="noopener noreferrer"
      referrerPolicy="origin"
    >
      <img
        src="https://trustseal.enamad.ir/logo.aspx?id=7272343&Code=cOtSN7wMbB6CVNFOfGbN4ZQHPTjttU0K"
        alt={COPY.site.enamadAlt}
        referrerPolicy="origin"
        className="h-16 w-auto cursor-pointer"
        // `code` is enamad.ir's attribute, not React's — not in `ImgHTMLAttributes`, but a
        // lowercase unknown attribute still passes straight through to the DOM.
        {...({ code: "cOtSN7wMbB6CVNFOfGbN4ZQHPTjttU0K" } as Record<string, string>)}
      />
    </a>
  );
}

export function SiteFooter() {
  return (
    <footer className="mx-auto mt-xxxl flex max-w-[1040px] flex-wrap items-center justify-between gap-md border-t border-ink/8 px-lg pt-xl pb-xl md:px-xl">
      <span className="flex items-center gap-sm">
        <Mark size={26} />
        <span className="text-small text-muted">kiddora.ir</span>
      </span>
      <EnamadBadge />
    </footer>
  );
}

/**
 * The page body for surfaces that are one readable column.
 *
 * The landing page does NOT use this — it manages its own full-bleed sections, because a page
 * built entirely of one centred column is the shape that made the previous version read as a
 * document rather than a designed page.
 */
export function SitePage({ children }: { children: React.ReactNode }) {
  return <main className="mx-auto max-w-[720px] px-lg md:px-xl">{children}</main>;
}
